import { useState, useEffect } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { ALL_TABS, type TabKey } from "@/pages/admin/system/constants";
import NotificationPanel from "@/pages/admin/system/components/NotificationPanel";
import ScheduledPanel from "@/pages/admin/system/components/ScheduledPanel";
import NotificationHistoryPanel from "@/pages/admin/system/components/NotificationHistoryPanel";
import OperationLogsPanel from "@/pages/admin/system/components/OperationLogsPanel";
import RolesPanel from "@/pages/admin/system/components/RolesPanel";
import EmployeesPanel from "@/pages/admin/system/components/EmployeesPanel";

export default function AdminSystemPage() {
  const {
    fetchClients, sendBulkNotification, fetchNotificationHistory,
    fetchScheduledNotifications, cancelScheduledNotification, sendScheduledNow, updateScheduledNotification,
    fetchRoles, createRole, updateRole, deleteRole,
    fetchEmployees, createEmployee, updateEmployee, deleteEmployee,
    hasPermission, isGlobalView, hasSystemSubPermission,
    fetchOperationLogs, exportOperationLogs,
  } = useAdminAuth();

  const filteredTabs = ALL_TABS.filter((tab) => {
    if (!hasPermission(tab.perm)) return false;
    if (!tab.sub) return true;
    return hasSystemSubPermission(tab.sub);
  });

  const [activeTab, setActiveTab] = useState<TabKey>(
    (filteredTabs.find((t) => t.key === "notification")?.key as TabKey) || (filteredTabs[0]?.key as TabKey) || "notification"
  );

  useEffect(() => {
    if (!filteredTabs.find((t) => t.key === activeTab)) {
      const firstAvailable = filteredTabs[0]?.key as TabKey;
      if (firstAvailable) setActiveTab(firstAvailable);
    }
  }, [activeTab, filteredTabs]);

  const [message, setMessage] = useState("");
  const [selectedClients, setSelectedClients] = useState<number[]>([]);

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(""), message.includes("失败") || message.includes("错误") ? 4000 : 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const handleScheduledSent = () => {
    // Signal to scheduled panel to refresh — handled by component mount
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[#1a3a5c]">系统管理</h1>
        <p className="text-xs text-gray-400 mt-1">管理消息推送、角色权限与员工账号</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 text-sm flex items-center gap-2 ${message.includes("成功") || message.includes("已") ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
          <i className={message.includes("成功") || message.includes("已") ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {message}
        </div>
      )}

      <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm flex-wrap">
        {filteredTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabKey)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap min-w-0 ${activeTab === tab.key ? "bg-[#1a3a5c] text-white" : "text-gray-500 hover:bg-gray-50"}`}
          >
            <i className={tab.icon}></i>{tab.label}
          </button>
        ))}
      </div>

      {activeTab === "notification" && (
        <NotificationPanel
          fetchClients={fetchClients}
          sendBulkNotification={sendBulkNotification}
          onScheduledSent={handleScheduledSent}
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          onMessage={setMessage}
        />
      )}

      {activeTab === "scheduled" && (
        <ScheduledPanel
          fetchScheduledNotifications={fetchScheduledNotifications}
          cancelScheduledNotification={cancelScheduledNotification}
          sendScheduledNow={sendScheduledNow}
          updateScheduledNotification={updateScheduledNotification}
          isGlobalView={isGlobalView}
          onMessage={setMessage}
        />
      )}

      {activeTab === "notification_history" && (
        <NotificationHistoryPanel
          fetchNotificationHistory={fetchNotificationHistory}
          selectedClients={selectedClients}
          isGlobalView={isGlobalView}
        />
      )}

      {activeTab === "logs" && (
        <OperationLogsPanel
          fetchOperationLogs={fetchOperationLogs}
          exportOperationLogs={exportOperationLogs}
          onMessage={setMessage}
        />
      )}

      {activeTab === "roles" && (
        <RolesPanel
          fetchRoles={fetchRoles}
          createRole={createRole}
          updateRole={updateRole}
          deleteRole={deleteRole}
          onMessage={setMessage}
        />
      )}

      {activeTab === "employees" && (
        <EmployeesPanel
          fetchEmployees={fetchEmployees}
          createEmployee={createEmployee}
          updateEmployee={updateEmployee}
          deleteEmployee={deleteEmployee}
          fetchRoles={fetchRoles}
          onMessage={setMessage}
        />
      )}
    </div>
  );
}